import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import GoogleIdentityService from '~/modules/services/google-identity-service';

/**
 * Google's four-colour "G", as published in their branding guidelines. Inlined rather than
 * fetched: it is the one mark we are not allowed to redraw or recolour, and a login screen
 * should not wait on a network request to show a button.
 */
const GoogleMark = () => (
	<svg className="px-auth-modal-google-mark" viewBox="0 0 48 48" aria-hidden="true" focusable="false">
		<path fill="#ea4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
		<path fill="#4285f4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
		<path fill="#fbbc05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
		<path fill="#34a853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
	</svg>
);

/**
 * "Google" for the login modal — our markup, so it matches the form above it.
 *
 * Clicking opens Google's account chooser in a popup and reports the one-time authorization
 * code it returns. Renders nothing when no client ID is configured, or when Google's library
 * cannot be loaded at all: the password form is the way in either way.
 *
 * @param {Function} onCode - receives the authorization code to exchange for a session
 * @param {boolean} [disabled] - true while a sign-in is already in flight
 */
const GoogleSignInButton = ({ onCode, disabled }) => {
	const [ready, setReady] = useState(false);
	const [waiting, setWaiting] = useState(false);

	useEffect(() => {
		let cancelled = false;

		// Both up front, so the click that opens the popup is still inside the browser's
		// user-activation window: a popup opened after awaiting a network round trip is
		// exactly the kind a popup blocker swallows. The client ID arrives with the boot
		// `system-check` request, which is why this is asynchronous at all — and why the
		// button appears a moment after the modal rather than with it.
		GoogleIdentityService.clientId()
			.then((clientId) => {
				if(cancelled || !clientId) {
					return null;
				}
				return GoogleIdentityService.load().then(() => {
					if(!cancelled) {
						setReady(true);
					}
				});
			})
			.catch(() => { /* no client ID, or Google unreachable: offer nothing */ });

		return () => {
			cancelled = true;
		};
	}, []);

	const handleClick = async() => {
		setWaiting(true);

		try {
			onCode(await GoogleIdentityService.requestCode());
		}
		catch(e) {
			// Closing the popup is a decision, not a failure, so nothing is said about it.
			// An origin Google does not recognise arrives here too, and that one is worth a
			// word in the console — it is otherwise indistinguishable from a cancel.
			console.warn(
				`Google sign-in did not complete (${e?.message}). If this was not a cancelled `
				+ `popup, check that ${window.location.origin} is an Authorized JavaScript `
				+ 'origin for the client ID.'
			);
		}
		finally {
			setWaiting(false);
		}
	};

	if(!ready) {
		return null;
	}

	return (
		<div className="px-auth-modal-google">
			<div className="px-auth-modal-divider"><span>or</span></div>
			<button
				type="button"
				className="px-auth-modal-google-button"
				onClick={handleClick}
				disabled={disabled || waiting}
				data-cy="google-signin"
				data-testid="google-signin"
			>
				<GoogleMark/>
				<span>Google</span>
			</button>
		</div>
	);
};

GoogleSignInButton.propTypes = {
	onCode: PropTypes.func.isRequired,
	disabled: PropTypes.bool
};

export default GoogleSignInButton;
